import React, { useState } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";

const DesignHub = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [demoStep, setDemoStep] = useState(0);

  const breadcrumbItems = [
    { label: "Marketing & Sales", link: "/marketing-sales" },
    { label: "Design Hub", link: "/marketing-sales/design-hub" },
  ];

  const templates = [
    {
      id: 1,
      name: "Business Card",
      category: "Print",
      thumbnail: "/templates/business-card.jpg",
    },
    {
      id: 2,
      name: "Newsletter",
      category: "Digital",
      thumbnail: "/templates/newsletter.jpg",
    },
    {
      id: 3,
      name: "Social Media Post",
      category: "Digital",
      thumbnail: "/templates/social-post.jpg",
    },
    {
      id: 4,
      name: "Event Flyer",
      category: "Print",
      thumbnail: "/templates/event-flyer.jpg",
    },
    {
      id: 5,
      name: "Email Header",
      category: "Digital",
      thumbnail: "/templates/email-header.jpg",
    },
    {
      id: 6,
      name: "Presentation",
      category: "Digital",
      thumbnail: "/templates/presentation.jpg",
    },
  ];

  const features = [
    {
      title: "Template Management",
      items: [
        {
          icon: (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
            </svg>
          ),
          name: "Pre-Designed Templates",
          description:
            "Explore templates for business cards, event flyers, ads, newsletters, and more.",
        },
        {
          icon: (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
          ),
          name: "Custom Template Creation",
          description:
            "Design and save custom templates to maintain brand consistency.",
        },
      ],
    },
    {
      title: "Design Tools",
      items: [
        {
          icon: (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
            </svg>
          ),
          name: "Drag-and-Drop Interface",
          description:
            "Create and customize materials easily, no design skills needed.",
        },
        {
          icon: (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z" />
            </svg>
          ),
          name: "Asset Library",
          description:
            "Access a repository of images, icons, fonts, and brand assets.",
        },
      ],
    },
    {
      title: "Brand Management",
      items: [
        {
          icon: (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8z" />
            </svg>
          ),
          name: "Brand Kit",
          description:
            "Manage colors, logos, fonts, and style guides in one place.",
        },
        {
          icon: (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          ),
          name: "Consistency Checks",
          description: "Ensure your designs always align with brand standards.",
        },
      ],
    },
  ];

  const testimonials = [
    {
      quote:
        "Design Hub has transformed how we create marketing materials. It's intuitive and saves us hours of work.",
      author: "Sarah Johnson",
      role: "Financial Advisor",
      company: "Wealth Management Partners",
      image: "/testimonials/sarah.jpg",
    },
    {
      quote:
        "The template library is extensive and the brand management tools keep our materials consistent.",
      author: "Michael Chen",
      role: "Senior Financial Planner",
      company: "Pacific Investment Group",
      image: "/testimonials/michael.jpg",
    },
    {
      quote:
        "Collaboration features make it easy to work with our marketing team and get approvals quickly.",
      author: "Emily Rodriguez",
      role: "Marketing Director",
      company: "Capital Advisors",
      image: "/testimonials/emily.jpg",
    },
  ];

  const actionButton = (
    <button
      onClick={() => {}}
      className="px-6 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529] transition-colors"
    >
      Get Started Now
    </button>
  );

  return (
    <SubPageLayout
      title="Design Hub"
      subtitle="A creative space for managing and designing marketing materials with ease, tailored for financial advisors."
      breadcrumbItems={breadcrumbItems}
      actionButton={actionButton}
    >
      {/* Feature Highlights Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
          Why Choose Design Hub?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((category, idx) => (
            <div key={idx} className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {category.title}
              </h3>
              <div className="space-y-6">
                {category.items.map((feature, featureIdx) => (
                  <div key={featureIdx} className="flex items-start">
                    <div className="flex-shrink-0 text-[#202EC6]">
                      {feature.icon}
                    </div>
                    <div className="ml-4">
                      <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                        {feature.name}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Template Gallery Section */}
      <section className="mb-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Popular Templates
          </h2>
          <button className="text-[#202EC6] hover:text-opacity-80">
            View All Templates
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="group relative bg-surface-default dark:bg-surface-default rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md dark:shadow-none transition-shadow"
            >
              <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                {/* Placeholder for template thumbnail */}
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400">{template.name}</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                  {template.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-500">{template.category}</p>
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity">
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529] transition-colors">
                    Use Template
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="mb-16 bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          See Design Hub in Action
        </h2>
        <div className="aspect-w-16 aspect-h-9">
          <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
            <span className="text-gray-500 dark:text-gray-500">Interactive Demo Coming Soon</span>
          </div>
        </div>
      </section>

      {/* Workflow Integration Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
          Streamline Your Marketing Workflows
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Seamless Integrations
            </h3>
            <ul className="space-y-4">
              <li className="flex items-center">
                <svg
                  className="w-5 h-5 text-[#029831] mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">CRM Integration</span>
              </li>
              <li className="flex items-center">
                <svg
                  className="w-5 h-5 text-[#029831] mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">Social Media Publishing</span>
              </li>
              <li className="flex items-center">
                <svg
                  className="w-5 h-5 text-[#029831] mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">Email Marketing Platforms</span>
              </li>
            </ul>
          </div>
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Export Options
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">📄</span>
                <span className="text-gray-700 dark:text-gray-300">PDF</span>
              </div>
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">🖼️</span>
                <span className="text-gray-700 dark:text-gray-300">PNG</span>
              </div>
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">📸</span>
                <span className="text-gray-700 dark:text-gray-300">JPG</span>
              </div>
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">🎨</span>
                <span className="text-gray-700 dark:text-gray-300">SVG</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
          What Our Clients Say
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, idx) => (
            <div key={idx} className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0">
                  {/* Placeholder for testimonial image */}
                </div>
                <div className="ml-4">
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {testimonial.author}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-500">{testimonial.role}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">{testimonial.company}</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 italic">"{testimonial.quote}"</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Footer Section */}
      <section className="text-center py-16 bg-gray-50 rounded-lg">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Ready to Elevate Your Marketing?
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
          Start designing with Design Hub today!
        </p>
        <button className="px-8 py-3 bg-[#029831] text-white rounded-lg hover:bg-[#018529] transition-colors text-lg">
          Sign Up Now
        </button>
      </section>
    </SubPageLayout>
  );
};

export default DesignHub;
